import { cn } from "@/lib/utils"

/**
 * Shared surface + motion vocabulary for the dashboard.
 *
 * Replaces the old per-file `card3d` / `btn3d` strings, which baked
 * `rgba(0,0,0,…)` inset shadows into every card. Those were tuned for the dark
 * theme and read as muddy smudges on the four light themes. Everything here is
 * driven by design tokens instead, so it resolves correctly in `dark`,
 * `dsat-school`, `ielts`, `custom` and `custom-dark`.
 */

/**
 * Visible keyboard focus on every interactive element.
 *
 * Rings on `--primary`, not `--ring`: `--ring` is a mid grey that measures
 * 2.48:1 against the light sidebar and 3.79:1 against the dark one, while
 * `--primary` clears 3:1 in all six themes (3.89 worst case, in `dsat-school`).
 */
export const focusRing =
    "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"

/** Same ring, offset against the sidebar surface rather than the page. */
export const sidebarFocusRing =
    "outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar"

/** Resting card: flat, token-driven, no fake bevel. */
export const surface = "rounded-2xl border border-border bg-card"

/**
 * Card that is itself a link or button. Lift and shadow are the hover signal;
 * both are wrapped in `motion-safe:` so `prefers-reduced-motion` users get the
 * colour change only and nothing moves.
 */
export const interactiveSurface = cn(
    surface,
    focusRing,
    "cursor-pointer",
    "transition-[border-color,box-shadow,transform] duration-200 ease-out",
    "hover:border-primary/40",
    "motion-safe:hover:-translate-y-0.5 motion-safe:hover:shadow-lg motion-safe:hover:shadow-primary/5",
    "motion-safe:active:translate-y-0 motion-safe:active:shadow-none"
)

/** Compact control in the page header / utility cluster. */
export const utilityControl = cn(
    "inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card",
    "text-muted-foreground hover:text-foreground hover:border-primary/40",
    "transition-colors duration-150",
    focusRing,
    "cursor-pointer disabled:cursor-not-allowed disabled:opacity-50"
)

/** Min touch target — 44px, per Apple HIG / WCAG 2.2 target size. */
export const touchTarget = "min-h-11 min-w-11"

/**
 * Expands a control's *pointer* target to 44x44 without touching its drawn box
 * or the surrounding layout.
 *
 * ⚠️ **WCAG 2.2 AA (2.5.8) MEASURES THE AREA THAT ACCEPTS THE POINTER, NOT THE
 * PAINTED ONE**, so a centred out-of-flow pseudo element satisfies it while the
 * control keeps its visual weight. Unlike `touchTarget` this costs the layout
 * nothing, which is what makes it the right tool for a small control sitting in
 * a dense row — the planner's done-toggle draws at 20x20 and must stay that
 * size to keep the row rhythm, but it is the most-repeated action on the page.
 */
export const hitArea =
    "relative before:absolute before:left-1/2 before:top-1/2 before:h-11 before:w-11 " +
    "before:-translate-x-1/2 before:-translate-y-1/2 before:content-['']"

/** Section label above a group of cards. */
export const sectionLabel =
    "text-xs font-semibold uppercase tracking-widest text-muted-foreground"
